import {
  ChannelBinding,
  ChannelBindingMode,
  ChannelBindingRQ,
  ChannelSpr,
  RateCard,
} from '@/models';
import { Channel } from '@/models/ChannelModels';

export function generateSPRProductID(spr: ChannelSpr): string {
  return `${spr.supplier.id}-${spr.product.id}`;
}

export function generateBindingProductID(binding: ChannelBinding): string {
  return `${binding.supplier.id}-${binding.product.id}`;
}

/**
 * hasChannelBindings - checks to see if there are any bindings for the SPR
 * @param spr
 * @param bindings
 */
export function hasChannelBindings(spr: ChannelSpr, bindings: ChannelBinding[]): boolean {
  const idSet = new Set(bindings.map((binding) => generateBindingProductID(binding)));
  return idSet.has(generateSPRProductID(spr));
}

export function bindingHasRate(binding: ChannelBinding, sprRateId: string): boolean {
  return !!binding.rateIds.find((id) => id === sprRateId);
}

/**
 * constructBindingRequests - builds exactly one request per product for the selected
 * channel. A product already bound on that channel is UPDATEd with the merged rate IDs,
 * otherwise it is CREATEd. Bindings on other channels are ignored.
 * @param rateCard
 * @param sprs
 * @param bindings
 * @param channel
 */
export function constructBindingRequests(
  rateCard: RateCard,
  sprs: ChannelSpr[],
  bindings: ChannelBinding[],
  channel: Channel,
): ChannelBindingRQ[] {
  const sprsByProduct = new Map<string, ChannelSpr[]>();
  for (const spr of sprs) {
    const key = generateSPRProductID(spr);
    sprsByProduct.set(key, [...(sprsByProduct.get(key) || []), spr]);
  }
  const channelBindings = bindings.filter((b) => b.channel.id === channel.id);
  return [...sprsByProduct.entries()].map(([key, group]) => {
    const { supplier, product } = group[0];
    const existing = channelBindings.find((b) => generateBindingProductID(b) === key);
    const rateIDs = [
      ...new Set([...(existing?.rateIds || []), ...group.map((spr) => spr.rate.id)]),
    ];
    return {
      mode: existing ? ChannelBindingMode.UPDATE : ChannelBindingMode.CREATE,
      channelID: channel.id || '',
      channelName: channel.name || '',
      supplierID: supplier.id,
      productID: product.id,
      rateIDs,
      rateCardID: rateCard.id,
      validFrom: '',
      validUntil: '',
    };
  });
}

/**
 * filterChannelBindingRequestsByMode - returns the set of requests for the mode
 * @param bindings
 * @param mode
 */
export function filterChannelBindingRequestsByMode(
  bindings: ChannelBindingRQ[],
  mode: ChannelBindingMode,
): ChannelBindingRQ[] {
  return bindings.filter((binding) => {
    return binding.mode == mode;
  });
}

/**
 * filterBindingsByChannelID - filters the bindings that match the channel ID
 * @param channelID
 * @param bindings
 */
export function filterBindingsByChannelID(
  channelID: string,
  bindings: ChannelBinding[],
): ChannelBinding[] {
  const filtered = bindings.filter((binding: ChannelBinding) => {
    return binding.channel.id === channelID;
  });
  return filtered;
}
